import { digitizationLayerIds } from './digitizationLayers';
import layerConfigStore from './layerConfig';
import { allSpecialDatasetLayerIds } from './specialDatasets';
import { UtilityLineLayers, UtilityPointAndPolygonLayers, UtilityServiceLayers } from './TOCLayers';

// Layers unverified users (`rootStore.inUnverifiedMode`) must not access: proprietary
// utility infrastructure, digitized layers, and special datasets. One predicate shared by
// the picker (disabled checkboxes) and the map renderer (the backstop that strips these
// from what actually draws) — the two must never disagree on what "protected" means.
//
// Pure module, no React / no I/O — unit-testable. Kept out of TOCLayers.ts/layers.ts to
// stay clear of their import cycle (this imports both sides; nothing here is imported back).

// The code-side utility id arrays are genuinely static (never refilled from the DB), so a
// Set snapshot at module load is safe — unlike the digitization/special-dataset containers
// read live below.
const STATIC_UTILITY_LAYER_IDS: ReadonlySet<string> = new Set([
  ...UtilityLineLayers,
  ...UtilityPointAndPolygonLayers,
  ...UtilityServiceLayers,
]);

/**
 * True when `layerId` must be blocked for unverified users. Union of:
 * - the static utility layer id arrays;
 * - the **live** `digitizationLayerIds` / `allSpecialDatasetLayerIds` containers, read at
 *   call time — the DB layer-config snapshot refills them in place after module load, so a
 *   spread-at-import copy (the old `AllUtilityLayers` shape) would silently miss every
 *   DB-added digitized layer and special dataset;
 * - the catalog-derived protected leaves (`layerConfigStore.isProtectedLeafId`), which
 *   covers DB-added utility leaves the code-side lists have never heard of.
 */
export function isProtectedLayerId(layerId: string): boolean {
  return (
    STATIC_UTILITY_LAYER_IDS.has(layerId) ||
    digitizationLayerIds.includes(layerId) ||
    allSpecialDatasetLayerIds.has(layerId) ||
    layerConfigStore.isProtectedLeafId(layerId)
  );
}

/**
 * True when a picker section must be blocked for unverified users: it has leaves and every
 * one is protected. Derived from descendants — not display labels — so DB-renamed sections
 * stay guarded, and mixed sections (e.g. Bookmarked) stay enabled while their protected
 * leaves individually disable.
 */
export function isProtectedSection(descendantLeafIds: readonly string[]): boolean {
  return descendantLeafIds.length > 0 && descendantLeafIds.every(isProtectedLayerId);
}

/** The ids from `layerIds` that are safe to render for unverified users. */
export function filterProtectedLayerIds(layerIds: Iterable<string>): Set<string> {
  return new Set([...layerIds].filter((id) => !isProtectedLayerId(id)));
}

/**
 * True while the unverified-mode renderer must hold (draw nothing) instead of filtering
 * until the protected catalog (`catalogVersion === 0` until it first arrives) is ready.
 */
export function isAwaitingProtectedCatalog(catalogVersion: number): boolean {
  return catalogVersion === 0;
}
